import { useCallback, useId, useRef, useState } from 'react';
import type { LayoutNavItem } from '@portal/platform/configuration';
import { useSession } from '@portal/platform/session';
import { AppLink } from '../app-link';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Icon, type IconName } from '../kit/icon';
import { useDismiss } from './use-dismiss';

/** The backend's id for the logout entry: it has no route, it is an action. */
export const LOGOUT_ITEM_ID = 'logout';

/** Font Awesome glyphs for the layout's header entries (the backend sends Material names). */
const ITEM_ICONS: Record<string, IconName> = {
  profile: 'user',
  'change-password': 'key',
  [LOGOUT_ITEM_ID]: 'lock',
};

function initials(name: string | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]!.toUpperCase())
    .join('');
}

/**
 * The legacy account dropdown: a 35px avatar and the user's name, opening a menu headed
 * "Konto" with the layout's header items (profile, change password, log out). A GLOBAL admin
 * also gets a "Brugervisning" switch there: off sees everything, on sees the node as its users do.
 */
export function UserMenu({ items, onLogout }: { items: LayoutNavItem[]; onLogout: () => void }) {
  const t = useShellTexts().header;
  const { identity, scope, platform } = useSession();
  const scoped = scope.viewMode === 'scoped';
  const scopedLabelId = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  const entryClass =
    'flex w-full items-center gap-2 border-b border-line px-5 py-2.5 text-left text-sm text-ink no-underline last:border-b-0 hover:bg-cap hover:text-ink';
  const icon = (item: LayoutNavItem) => (
    <Icon name={ITEM_ICONS[item.id] ?? 'caret-right'} className="w-4 text-ink-muted" />
  );
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t.userMenu}
        onClick={() => setOpen(value => !value)}
        className="flex items-center gap-2 rounded-bs px-2 py-1 text-sm text-ink-soft hover:text-ink"
      >
        <span
          aria-hidden="true"
          className="flex h-[35px] w-[35px] items-center justify-center rounded-full bg-line text-xs font-bold text-ink-soft"
        >
          {initials(identity?.name) || <Icon name="user" />}
        </span>
        <span>{identity?.name ?? t.userMenu}</span>
        <Icon name="caret-down" className="text-xs" />
      </button>
      {open ? (
        <div
          role="group"
          aria-label={t.account}
          className="absolute top-full right-0 z-[1000] mt-0.5 min-w-[180px] overflow-hidden rounded-bs border border-black/15 bg-white"
        >
          <div className="border-b border-line bg-cap px-5 py-2 text-center text-sm font-bold text-ink">
            {t.account}
          </div>
          {scope.canToggleViewMode ? (
            <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-2.5 text-sm text-ink">
              <span id={scopedLabelId} className="flex items-center gap-2">
                <Icon name="users" className="w-4 text-ink-muted" />
                {t.scopedView}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={scoped}
                aria-labelledby={scopedLabelId}
                onClick={() => platform.scope.setViewMode(scoped ? 'admin' : 'scoped')}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${scoped ? 'border-brand-border bg-brand' : 'border-line bg-line'}`}
              >
                <span
                  aria-hidden="true"
                  className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition-all ${scoped ? 'right-0.5' : 'left-0.5'}`}
                />
              </button>
            </div>
          ) : null}
          <ul>
            {items.map(item =>
              item.id === LOGOUT_ITEM_ID ? (
                <li key={item.id}>
                  <button
                    type="button"
                    className={entryClass}
                    onClick={() => {
                      setOpen(false);
                      onLogout();
                    }}
                  >
                    {icon(item)}
                    {item.label}
                  </button>
                </li>
              ) : item.route ? (
                <li key={item.id}>
                  <AppLink href={item.route} className={entryClass} onClick={() => setOpen(false)}>
                    {icon(item)}
                    {item.label}
                  </AppLink>
                </li>
              ) : null,
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
